import {
  createSendingDomain,
  createTrackingDomain,
  deleteSendingDomain,
  deleteTrackingDomain,
  editSendingDomain,
  editTrackingDomain,
  enableDeepLinkPath,
  getDeepLinks,
  getMailDomains,
  upsertAndroidAssets,
  upsertDeepLinkPath,
} from 'api/MailDomainsAPI';
import { MAIL_DOMAINS_ERROR_CODES } from 'constants/errorCodes';
import { addNegativeToast, addPositiveToast } from 'modules/toastContainer';
import { push } from 'redux-first-history';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { addToRootSaga } from 'store';
import { getErrorResponse } from 'utils/xhr';
import * as actions from './actions';
import * as actionTypes from './actionTypes';
import {
  DEFAULT_ERROR,
  TRACKING_DOMAIN_ERROR,
  TRACKING_HTTPS_ERROR,
  TRACKING_HTTPS_SUCCESS,
} from './constants';
import {
  getAppSiteAssociations,
  getAssetLinks,
  getSendingDomains,
  getTrackingDomains,
  selectEnabledDestinationDomainAppSiteAssociationsByTrackingDomain,
} from './selectors';
import {
  createNewTrackingDomain,
  joinUpdatedDomainIntoDomains,
  joinUpdatedLegacyAndDestinationAASAIntoDomains,
  setDefaultTrackingDomain,
} from './utils';

/**
 * fetchMailDomainsSaga
 * Saga for fetching mail domains
 * @return { void }
 */
export function* fetchMailDomainsSaga() {
  try {
    const { data } = yield call(getMailDomains);

    const {
      espAccounts,
      sendingDomains,
      trackingDomains,
      verificationErrors,
      linkShorteningTemplateIds,
      defaultSmsLinkShorteningDomain,
    } = data;
    yield put(
      actions.fetchMailDomainsResponse(
        espAccounts,
        sendingDomains,
        trackingDomains,
        verificationErrors,
        linkShorteningTemplateIds,
        defaultSmsLinkShorteningDomain,
      ),
    );
  } catch (error) {
    const err = getErrorResponse(error);
    yield put(actions.fetchMailDomainsError(err));
    yield put(addNegativeToast(DEFAULT_ERROR));
  }
}

/**
 * createSendingDomainSaga
 * Saga for creating a new sending domain
 * @return { void }
 */
export function* createSendingDomainSaga({ sendingDomain, espAccountId }) {
  try {
    const { data } = yield call(createSendingDomain, sendingDomain, espAccountId);
    yield put(actions.createSendingDomainResponse(data));
  } catch (error) {
    const err = getErrorResponse(error);
    yield put(actions.createSendingDomainError(err));
    yield put(addNegativeToast(DEFAULT_ERROR));
  }
}

/**
 * editSendingDomainSaga
 * Saga for creating a new sending domain
 * @return { void }
 */
export function* editSendingDomainSaga({ sendingDomain, successMessage }) {
  try {
    yield call(editSendingDomain, sendingDomain);

    const sendingDomains = yield select(getSendingDomains);
    const updatedDomains = joinUpdatedDomainIntoDomains(sendingDomains, sendingDomain, 'id');

    yield put(addPositiveToast(successMessage));
    yield put(actions.editSendingDomainResponse(updatedDomains));
  } catch (error) {
    const err = getErrorResponse(error);
    yield put(actions.editSendingDomainError(err));
    yield put(addNegativeToast(DEFAULT_ERROR));
  }
}

/**
 * deleteSendingDomainSaga
 * Saga for creating a new sending domain
 * @return { void }
 */
export function* deleteSendingDomainSaga({ sendingDomainId }) {
  try {
    yield call(deleteSendingDomain, sendingDomainId);
    yield put(addPositiveToast('Deleted sending domain'));
    yield put(actions.deleteSendingDomainResponse(sendingDomainId));
  } catch (error) {
    const errorMessage = getErrorResponse(error);
    yield put(actions.deleteSendingDomainError(errorMessage));

    const errorCode = error.response?.data?.code || '';
    if (errorMessage.length && errorCode === MAIL_DOMAINS_ERROR_CODES.sendingDomainIsInUse)
      yield put(addNegativeToast(errorMessage));
    else yield put(addNegativeToast(DEFAULT_ERROR));
  }
}

/**
 * createTrackingDomainSaga
 * Saga for creating a new sending domain
 * @return { void }
 */
export function* createTrackingDomainSaga({ trackingDomain }) {
  try {
    yield call(createTrackingDomain, trackingDomain);
    yield put(addPositiveToast('Tracking domain created'));
    yield put(actions.createTrackingDomainResponse(createNewTrackingDomain(trackingDomain)));
    yield put(push(`/settings/domains/${trackingDomain}`));
  } catch (error) {
    const err = getErrorResponse(error);
    yield put(actions.createTrackingDomainError(err));
    yield put(addNegativeToast(DEFAULT_ERROR));
  }
}

/**
 * editTrackingDomainSaga
 * Saga for creating a new sending domain
 * @return { void }
 */
export function* editTrackingDomainSaga({ trackingDomain }) {
  try {
    yield call(editTrackingDomain, trackingDomain);
    yield put(addPositiveToast(TRACKING_HTTPS_SUCCESS));
    // return the name
    const trackingDomains = yield select(getTrackingDomains);
    const updatedDomains = joinUpdatedDomainIntoDomains(trackingDomains, trackingDomain, 'domain');
    yield put(actions.editTrackingDomainResponse(updatedDomains));
  } catch (error) {
    const err = getErrorResponse(error);
    yield put(actions.editTrackingDomainError(err));
    yield put(addNegativeToast(TRACKING_HTTPS_ERROR));
  }
}

/**
 * editTrackingDomainSaga
 * Saga for creating a new sending domain
 * @return { void }
 */
export function* setDefaultTrackingDomainSaga({ trackingDomain }) {
  try {
    yield call(editTrackingDomain, trackingDomain);
    yield put(addPositiveToast('Succesfully updated the default tracking domain.'));
    const trackingDomains = yield select(getTrackingDomains);
    const updatedDomains = setDefaultTrackingDomain(trackingDomains, trackingDomain);
    yield put(actions.setDefaultTrackingDomainResponse(updatedDomains));
  } catch (error) {
    const err = getErrorResponse(error);
    yield put(actions.setDefaultTrackingDomainError(err));
    yield put(addNegativeToast(DEFAULT_ERROR));
  }
}

/**
 * editTrackingDomainSaga
 * Saga for creating a new sending domain
 * @return { void }
 */
export function* deleteTrackingDomainSaga({ trackingDomainName }) {
  try {
    yield call(deleteTrackingDomain, trackingDomainName);
    yield put(addPositiveToast('Succesfully deleted the default tracking domain.'));
    const trackingDomains = yield select(getTrackingDomains);
    const updatedDomains = trackingDomains.filter(({ domain }) => domain !== trackingDomainName);
    yield put(actions.deleteTrackingDomainResponse(updatedDomains));
  } catch (error) {
    const err = getErrorResponse(error);
    yield put(actions.deleteTrackingDomainError(err));
    yield put(addNegativeToast(DEFAULT_ERROR));
  }
}

/**
 * fetchDeepLinksSaga
 * Saga for fetching mail domains
 * @return { void }
 */
export function* fetchDeepLinksSaga() {
  try {
    const { data } = yield call(getDeepLinks);
    const { androidAssetsLinks, appSiteAssociations } = data;
    yield put(actions.fetchDeepLinksResponse(androidAssetsLinks, appSiteAssociations));
  } catch (error) {
    const err = getErrorResponse(error);
    yield put(actions.fetchDeepLinksError(err));
    yield put(addNegativeToast(DEFAULT_ERROR));
  }
}

/**
 * createDeepLinkPathSaga
 * Saga for creating a new sending domain
 * @return { void }
 */
export function* createDeepLinkPathSaga({ appDomain, domain, json }) {
  try {
    yield call(upsertDeepLinkPath, appDomain, domain, json);
    const appSiteAssociations = yield select(getAppSiteAssociations);
    yield put(addPositiveToast('Destination domain created'));
    yield put(
      actions.createDeepLinkPathResponse([...appSiteAssociations, { appDomain, domain, json }]),
    );
    yield put(push(`/settings/domains/${domain}/${appDomain}`));
  } catch (error) {
    const err = getErrorResponse(error);
    yield put(actions.createDeepLinkPathError(err));
    yield put(addNegativeToast(DEFAULT_ERROR));
  }
}

/**
 * createDeepLinkPathSaga
 * Saga for creating a new sending domain
 * @return { void }
 */
export function* editDeepLinkPathSaga({ appDomain, domain, json }) {
  try {
    yield call(upsertDeepLinkPath, appDomain, domain, json);
    const appSiteAssociations = yield select(getAppSiteAssociations);
    yield put(addPositiveToast('apple-app-site-association file updated'));

    // the backend expects a string version of the json, which is then converted to actual JSON.
    // thus, when we fetch and store the JSON from the backend in the redux state
    // it needs to be the parsed json, rather than the stringified version
    const appleAppSiteAssociationFile = JSON.parse(json);
    const updatedAppSiteAssociations = joinUpdatedDomainIntoDomains(
      appSiteAssociations,
      { appDomain, domain, appleAppSiteAssociationFile },
      'domain',
      'appDomain',
    );
    yield put(actions.editDeepLinkPathResponse(updatedAppSiteAssociations));
    yield put(push(`/settings/domains/${domain}/${appDomain}`));
  } catch (error) {
    const err = getErrorResponse(error);
    yield put(actions.editDeepLinkPathError(err));
    yield put(addNegativeToast(DEFAULT_ERROR));
  }
}

/**
 * disableLegacyDeepLinkPathSaga
 * Saga for disabling legacy deeplinks saga
 * @return { void }
 */
export function* disableLegacyDeepLinkPathSaga({ appSiteAssociation }) {
  try {
    const { domain } = appSiteAssociation;
    yield call(enableDeepLinkPath, undefined, domain, false);
    const appSiteAssociations = yield select(getAppSiteAssociations);
    const updatedAssociations = joinUpdatedDomainIntoDomains(
      appSiteAssociations,
      { ...appSiteAssociation, appDomain: undefined, enabledForProject: false },
      'domain',
      'appDomain',
    );
    yield put(actions.enableDeepLinkPathResponse(updatedAssociations));
  } catch (error) {
    const err = getErrorResponse(error);
    yield put(actions.enableDeepLinkPathError(err));
    yield put(addNegativeToast(TRACKING_DOMAIN_ERROR));
  }
}

/**
 * disableDestinationDomainDeepLinkPathsSaga
 * Saga for disabling legacy deeplinks saga
 * @return { void }
 */
export function* disableDestinationDomainDeepLinkPathsSaga({ appSiteAssociation }) {
  try {
    const { domain } = appSiteAssociation;
    const enabledDestinationDomainAppSiteAssociation = yield select((state) =>
      selectEnabledDestinationDomainAppSiteAssociationsByTrackingDomain(state, {
        trackingDomainName: domain,
      }),
    );

    yield all(
      enabledDestinationDomainAppSiteAssociation.map(({ appDomain }) =>
        call(enableDeepLinkPath, appDomain, domain, false),
      ),
    );

    const appSiteAssociations = yield select(getAppSiteAssociations);
    const updatedAssociations = joinUpdatedLegacyAndDestinationAASAIntoDomains(
      appSiteAssociations,
      appSiteAssociation,
    );
    yield put(actions.enableDeepLinkPathResponse(updatedAssociations));
  } catch (error) {
    const err = getErrorResponse(error);
    yield put(actions.enableDeepLinkPathError(err));
    yield put(addNegativeToast(TRACKING_DOMAIN_ERROR));
  }
}

/**
 * enableDeepLinkPathSaga
 * Saga for creating a new sending domain
 * @return { void }
 */
export function* enableDeepLinkPathSaga({ appSiteAssociation, hasActiveLegacyDomain }) {
  try {
    const { appDomain, domain, enabledForProject } = appSiteAssociation;
    yield call(enableDeepLinkPath, appDomain, domain, enabledForProject);
    yield put(addPositiveToast(`Deep link ${enabledForProject ? 'enabled' : 'disabled'}`));

    const disableLegacyDomain = appDomain && enabledForProject && hasActiveLegacyDomain;
    const disableDestinationDomains = !appDomain && enabledForProject;

    if (disableLegacyDomain) {
      // side effect that happens when an appPath deeplink is enabled.
      // we want any legacy, or non-app-path deeplinks to be turned off.
      // the legacy files will all eventually be replaced by these appDomain apps.
      yield call(disableLegacyDeepLinkPathSaga, { appSiteAssociation });
    }

    if (disableDestinationDomains) {
      // side effect that happens when a legacy deeplink is enabled.
      // we want any non-legacy, or app-path deeplinks to be turned off.
      // the legacy files will all eventually be replaced by these appDomain apps.
      yield call(disableDestinationDomainDeepLinkPathsSaga, { appSiteAssociation });
    }

    const appSiteAssociations = yield select(getAppSiteAssociations);
    const updatedAssociations = joinUpdatedDomainIntoDomains(
      appSiteAssociations,
      appSiteAssociation,
      'domain',
      'appDomain',
    );

    yield put(actions.enableDeepLinkPathResponse(updatedAssociations));
  } catch (error) {
    const err = getErrorResponse(error);
    yield put(actions.enableDeepLinkPathError(err));
    yield put(addNegativeToast(DEFAULT_ERROR));
  }
}

/**
 * upsertAndroidAssetsSaga
 * Saga for creating a new sending domain
 * @return { void }
 */
export function* upsertAndroidAssetsSaga({ organizationId, domain, assetLinks }) {
  try {
    yield call(upsertAndroidAssets, organizationId, domain, assetLinks);
    yield put(addPositiveToast('asset-links.json file updated'));
    const androidAssetLinks = yield select(getAssetLinks);
    const updatedAndroidAssetLinks = joinUpdatedDomainIntoDomains(
      androidAssetLinks,
      { organizationId, domain, androidAssetLinks: assetLinks },
      'organizationId',
      'domain',
    );
    yield put(actions.upsertAndroidAssetLinksResponse(updatedAndroidAssetLinks));
  } catch (error) {
    const err = getErrorResponse(error);
    yield put(actions.upsertAndroidAssetLinksError(err));
    yield put(addNegativeToast(DEFAULT_ERROR));
  }
}

addToRootSaga([
  takeLatest(actionTypes.FETCH_MAIL_DOMAINS_REQUEST, fetchMailDomainsSaga),
  takeLatest(actionTypes.CREATE_SENDING_DOMAIN_REQUEST, createSendingDomainSaga),
  takeLatest(actionTypes.EDIT_SENDING_DOMAIN_REQUEST, editSendingDomainSaga),
  takeLatest(actionTypes.DELETE_SENDING_DOMAIN_REQUEST, deleteSendingDomainSaga),
  takeLatest(actionTypes.CREATE_TRACKING_DOMAIN_REQUEST, createTrackingDomainSaga),
  takeLatest(actionTypes.EDIT_TRACKING_DOMAIN_REQUEST, editTrackingDomainSaga),
  takeLatest(actionTypes.SET_DEFAULT_TRACKING_DOMAIN_REQUEST, setDefaultTrackingDomainSaga),
  takeLatest(actionTypes.DELETE_TRACKING_DOMAIN_REQUEST, deleteTrackingDomainSaga),
  takeLatest(actionTypes.FETCH_DEEP_LINKS_REQUEST, fetchDeepLinksSaga),
  takeLatest(actionTypes.CREATE_DEEP_LINK_PATH_REQUEST, createDeepLinkPathSaga),
  takeLatest(actionTypes.EDIT_DEEP_LINK_PATH_REQUEST, editDeepLinkPathSaga),
  takeLatest(actionTypes.ENABLE_DEEP_LINK_PATH_REQUEST, enableDeepLinkPathSaga),
  takeLatest(actionTypes.UPSERT_ANDROID_ASSET_LINKS_REQUEST, upsertAndroidAssetsSaga),
]);
